function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

/**
 * @license Copyright (c) 2003-2021, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module engine/model/textproxy
 */
import CKEditorError from '@ckeditor/ckeditor5-utils/src/ckeditorerror'; // @if CK_DEBUG_ENGINE // const { convertMapToStringifiedObject } = require( '../dev-utils/utils' );

/**
 * `TextProxy` represents a part of {@link module:engine/model/text~Text text node}.
 *
 * Since {@link module:engine/model/position~Position positions} can be placed between characters of a text node,
 * {@link module:engine/model/range~Range ranges} may contain only parts of text nodes. When {@link module:engine/model/range~Range#getItems
 * getting items}
 * contained in such range, we need to represent a part of that text node, since returning the whole text node would be incorrect.
 * `TextProxy` solves this issue.
 *
 * `TextProxy` has an API similar to {@link module:engine/model/text~Text Text} and allows to do most of the common tasks performed
 * on model nodes.
 *
 * **Note:** Some `TextProxy` instances may represent whole text node, not just a part of it.
 * See {@link module:engine/model/textproxy~TextProxy#isPartial}.
 *
 * **Note:** `TextProxy` is not an instance of {@link module:engine/model/node~Node node}. Keep this in mind when using it as a
 * parameter of methods.
 *
 * **Note:** `TextProxy` is a readonly interface. If you want to perform changes on model data represented by a `TextProxy`
 * use {@link module:engine/model/writer~Writer model writer API}.
 *
 * **Note:** `TextProxy` instances are created on the fly, basing on the current state of model. Because of this, it is
 * highly unrecommended to store references to `TextProxy` instances. `TextProxy` instances are not refreshed when
 * model changes, so they might get invalidated. Instead, consider creating {@link module:engine/model/liveposition~LivePosition live
 * position}.
 *
 * `TextProxy` instances are created by {@link module:engine/model/treewalker~TreeWalker model tree walker}. You should not need to create
 * an instance of this class by your own.
 */

var TextProxy = /*#__PURE__*/function () {
  /**
   * Creates a text proxy.
   *
   * @protected
   * @param {module:engine/model/text~Text} textNode Text node which part is represented by this text proxy.
   * @param {Number} offsetInText Offset in {@link module:engine/model/textproxy~TextProxy#textNode text node} from which the text proxy
   * starts.
   * @param {Number} length Text proxy length, that is how many text node's characters, starting from `offsetInText` it represents.
   * @constructor
   */
  function TextProxy(textNode, offsetInText, length) {
    _classCallCheck(this, TextProxy);

    /**
     * Text node which part is represented by this text proxy.
     *
     * @readonly
     * @member {module:engine/model/text~Text}
     */
    this.textNode = textNode;

    if (offsetInText < 0 || offsetInText > textNode.offsetSize) {
      /**
       * Given `offsetInText` value is incorrect.
       *
       * @error model-textproxy-wrong-offsetintext
       */
      throw new CKEditorError('model-textproxy-wrong-offsetintext', this);
    }

    if (length < 0 || offsetInText + length > textNode.offsetSize) {
      /**
       * Given `length` value is incorrect.
       *
       * @error model-textproxy-wrong-length
       */
      throw new CKEditorError('model-textproxy-wrong-length', this);
    }
    /**
     * Text data represented by this text proxy.
     *
     * @readonly
     * @member {String}
     */


    this.data = textNode.data.substring(offsetInText, offsetInText + length);
    /**
     * Offset in {@link module:engine/model/textproxy~TextProxy#textNode text node} from which the text proxy starts.
     *
     * @readonly
     * @member {Number}
     */

    this.offsetInText = offsetInText;
  }
  /**
   * Offset at which this text proxy starts in it's parent.
   *
   * @see module:engine/model/node~Node#startOffset
   * @readonly
   * @type {Number}
   */


  _createClass(TextProxy, [{
    key: "startOffset",
    get: function get() {
      return this.textNode.startOffset !== null ? this.textNode.startOffset + this.offsetInText : null;
    }
    /**
     * Offset size of this text proxy. Equal to the number of characters represented by the text proxy.
     *
     * @see module:engine/model/node~Node#offsetSize
     * @readonly
     * @type {Number}
     */

  }, {
    key: "offsetSize",
    get: function get() {
      return this.data.length;
    }
    /**
     * Offset at which this text proxy ends in it's parent.
     *
     * @see module:engine/model/node~Node#endOffset
     * @readonly
     * @type {Number}
     */

  }, {
    key: "endOffset",
    get: function get() {
      return this.startOffset !== null ? this.startOffset + this.offsetSize : null;
    }
    /**
     * Flag indicating whether `TextProxy` instance covers only part of the original {@link module:engine/model/text~Text text node}
     * (`true`) or the whole text node (`false`).
     *
     * This is `false` when text proxy starts at the very beginning of {@link module:engine/model/textproxy~TextProxy#textNode textNode}
     * ({@link module:engine/model/textproxy~TextProxy#offsetInText offsetInText} equals `0`) and text proxy sizes is equal to
     * text node size.
     *
     * @readonly
     * @type {Boolean}
     */

  }, {
    key: "isPartial",
    get: function get() {
      return this.offsetSize !== this.textNode.offsetSize;
    }
    /**
     * Parent of this text proxy, which is same as parent of text node represented by this text proxy.
     *
     * @readonly
     * @type {module:engine/model/element~Element|module:engine/model/documentfragment~DocumentFragment|null}
     */

  }, {
    key: "parent",
    get: function get() {
      return this.textNode.parent;
    }
    /**
     * Root of this text proxy, which is same as root of text node represented by this text proxy.
     *
     * @readonly
     * @type {module:engine/model/node~Node|module:engine/model/documentfragment~DocumentFragment}
     */

  }, {
    key: "root",
    get: function get() {
      return this.textNode.root;
    }
    /**
     * Checks whether this object is of the given.
     *
     *		textProxy.is( '$textProxy' ); // -> true
     *		textProxy.is( 'model:$textProxy' ); // -> true
     *
     *		textProxy.is( 'view:$textProxy' ); // -> false
     *		textProxy.is( 'range' ); // -> false
     *
     * {@link module:engine/model/node~Node#is Check the entire list of model objects} which implement the `is()` method.
     *
     * **Note:** Until version 20.0.0 this method wasn't accepting `'$textProxy'` type. The legacy `'textProxt'` type is still
     * accepted for backward compatibility.
     *
     * @param {String} type Type to check.
     * @returns {Boolean}
     */

  }, {
    key: "is",
    value: function is(type) {
      return type === '$textProxy' || type === 'model:$textProxy' || // This are legacy values kept for backward compatibility.
      type === 'textProxy' || type === 'model:textProxy';
    }
    /**
     * Gets path to this text proxy.
     *
     * @see module:engine/model/node~Node#getPath
     * @returns {Array.<Number>}
     */

  }, {
    key: "getPath",
    value: function getPath() {
      var path = this.textNode.getPath();

      if (path.length > 0) {
        path[path.length - 1] += this.offsetInText;
      }

      return path;
    }
    /**
     * Returns ancestors array of this text proxy.
     *
     * @param {Object} options Options object.
     * @param {Boolean} [options.includeSelf=false] When set to `true` this text proxy will be also included in parent's array.
     * @param {Boolean} [options.parentFirst=false] When set to `true`, array will be sorted from text proxy parent to root element,
     * otherwise root element will be the first item in the array.
     * @returns {Array} Array with ancestors.
     */

  }, {
    key: "getAncestors",
    value: function getAncestors() {
      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
        includeSelf: false,
        parentFirst: false
      };
      var ancestors = [];
      var parent = options.includeSelf ? this : this.parent;

      while (parent) {
        ancestors[options.parentFirst ? 'push' : 'unshift'](parent);
        parent = parent.parent;
      }

      return ancestors;
    }
    /**
     * Checks if this text proxy has an attribute for given key.
     *
     * @param {String} key Key of attribute to check.
     * @returns {Boolean} `true` if attribute with given key is set on text proxy, `false` otherwise.
     */

  }, {
    key: "hasAttribute",
    value: function hasAttribute(key) {
      return this.textNode.hasAttribute(key);
    }
    /**
     * Gets an attribute value for given key or `undefined` if that attribute is not set on text proxy.
     *
     * @param {String} key Key of attribute to look for.
     * @returns {*} Attribute value or `undefined`.
     */

  }, {
    key: "getAttribute",
    value: function getAttribute(key) {
      return this.textNode.getAttribute(key);
    }
    /**
     * Returns iterator that iterates over this node's attributes. Attributes are returned as arrays containing two
     * items. First one is attribute key and second is attribute value.
     *
     * This format is accepted by native `Map` object and also can be passed in `Node` constructor.
     *
     * @returns {Iterable.<*>}
     */

  }, {
    key: "getAttributes",
    value: function getAttributes() {
      return this.textNode.getAttributes();
    }
    /**
     * Returns iterator that iterates over this node's attribute keys.
     *
     * @returns {Iterable.<String>}
     */

  }, {
    key: "getAttributeKeys",
    value: function getAttributeKeys() {
      return this.textNode.getAttributeKeys();
    } // @if CK_DEBUG_ENGINE // toString() {
    // @if CK_DEBUG_ENGINE // 	return `#${ this.data }`;
    // @if CK_DEBUG_ENGINE // }
    // @if CK_DEBUG_ENGINE // log() {
    // @if CK_DEBUG_ENGINE // 	console.log( 'ModelTextProxy: ' + this );
    // @if CK_DEBUG_ENGINE // }
    // @if CK_DEBUG_ENGINE // logExtended() {
    // @if CK_DEBUG_ENGINE // 	console.log( `ModelTextProxy: ${ this }, ` +
    // @if CK_DEBUG_ENGINE // 		`attrs: ${ convertMapToStringifiedObject( this.getAttributes() ) }` );
    // @if CK_DEBUG_ENGINE // }

  }]);

  return TextProxy;
}();

export { TextProxy as default };